import {Engine} from "platform/engine/Engine";
import {
    AddSavedCreditCardPayload,
    DoSubmitDepositCCPayload,
    RemoveSavedCreditCardPayload,
    SetCreditCardId,
    SetDepositAmount,
    SetDepositConfiguration,
    SetDepositCurrency, CurrencyPayload,
    SetSavedCreditCards, SetReceiptDetails, DoSubmitDepositAPMPayload
} from "core/redux/deposit/DepositReduxActions";
import Platform from "platform/Platform";
import {PageType} from "enum/PageType";
import {HideLoader, NavigateTo, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {StoreState} from "core/redux/StoreState";
import {CreditCardState} from "core/redux/deposit/DepositReduxState";
import {HttpReject} from "platform/network/http/Http";
import {
    ApmPaymentMethod, BankDetail,
    DepositCreditCard,
    DepositCurrency,
    GetDepositConfigurationsResponse, PaymentProvider
} from "protocol/response/GetDepositConfigurationsResponse";
import Utils from "platform/util/Utils";
import {XhrManager} from "core/engine/XhrManager";
import WebUtil from "platform/util/WebUtil";
import {TSMap} from "typescript-map";
import {Currency} from "platform/enum/Currency";
import {SetCallMe} from "core/redux/app/AppReduxActions";
import {
    CreditCardClientDetails,
    CreditCardPaymentDetails,
    DepositByCreditCardRequest
} from "protocol/request/DepositByCreditCardRequest";
import {
    DepositByCreditCardError,
    DepositByCreditCardMetaData,
    DepositByCreditCardResponse
} from "protocol/request/DepositByCreditCardResponse";
import {FieldType} from "enum/FieldType";
import SubmitForm from "platform/network/form/SubmitForm";
import {PaymentMethod} from "enum/PaymentMethod";
import {ConvertPaymentAmountRequest} from "protocol/response/ConvertPaymentAmountRequest";
import {ConvertPaymentAmountResponse} from "protocol/response/ConvertPaymentAmountResponse";
import {TranslationKey} from "enum/TranslationKey";
import {StorageKey} from "enum/StorageKey";
import {DepositUtil} from "core/util/DepositUtil";
import {DepositByAPMRequest} from "protocol/request/DepositByAPMRequest";
import {DepositByAPMResponse} from "protocol/request/DepositByAPMResponse";
import {DepositVerifyAPMResponse} from "protocol/request/DepositVerifyAPMResponse";
import {TranslationParam} from "enum/TranslationParam";
import Parameter from "platform/util/Parameter";
import {LangCode} from "platform/enum/LangCode";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {ErrorCode} from "enum/ErrorCode";
import Translations from "platform/translation/Translations";

const QueryStringRaw: string = window.location.search;
const QueryString: string = QueryStringRaw.substring(1);
const UrlAmount: number = Utils.parseNumber(WebUtil.urlParam("amount")) || Utils.parseNumber(WebUtil.urlParam("packageAmount"));
const UrlCurrency: string = WebUtil.urlParam("currency");
const VerifyCC: boolean = Utils.parseBoolean(WebUtil.urlParam("verify"));
const VerifyAPM: boolean = Utils.parseBoolean(WebUtil.urlParam("eVerify"));

export default class DepositEngine extends Engine {

    private static _instance: DepositEngine;

    public static instance(): DepositEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        if (VerifyCC) {
            this._logger.debug("Verify 3D flow");
            const sPaymentDetails: string = await Platform.storage().getItem(StorageKey.CCPaymentDetails);
            const sClientDetails: string = await Platform.storage().getItem(StorageKey.CCClientDetails);
            const sMetaData: string = await Platform.storage().getItem(StorageKey.CCMetaData);
            if (sPaymentDetails && sMetaData) {
                try {
                    const paymentDetails: CreditCardPaymentDetails = JSON.parse(sPaymentDetails);
                    const clientDetails: CreditCardClientDetails = JSON.parse(sClientDetails);
                    const metaData: DepositByCreditCardMetaData = JSON.parse(sMetaData);
                    await this.doSubmitDepositCCInternal(paymentDetails, clientDetails, metaData, QueryString);
                } catch (e) {
                    this._logger.warn("Failed parse LS Metadata to verify 3D flow");
                }
            } else {
                this._logger.warn("Absent Metadata to verify 3D flow");
            }
        } else if (VerifyAPM) {
            await this.doVerifyDepositAPM();
        }
    }

    public onAppReady = async () => {
        this._logger.debug("Fetch deposit configurations");
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, GetDepositConfigurationsResponse] = await Utils.to(XhrManager.sendToBilling({}, "GetDepositConfigurations"));
        if (answer[0]) {
            this._logger.warn("Failed fetch deposit configurations");
        } else {
            const Currencies: TSMap<Currency, DepositCurrency> = new TSMap<Currency, DepositCurrency>();
            let InitialCurrency: DepositCurrency;
            if (Utils.isArrayNotEmpty(answer[1].Currencies)) {
                answer[1].Currencies.forEach((depositCurrency: DepositCurrency) => {
                    Currencies.set(depositCurrency.Name, depositCurrency);
                    if (depositCurrency.Name === UrlCurrency) {
                        InitialCurrency = depositCurrency;
                    }
                });
                if (Utils.isNull(InitialCurrency)) {
                    InitialCurrency = answer[1].Currencies[0];
                }
            }
            const {CreditCards, ApmPaymentMethods, BackToPlatformUrl, BankDetails, ClientDetails} = answer[1];
            const PaymentMethods: Array<string | number> = [];
            if (Utils.isArrayNotEmpty(CreditCards)) {
                PaymentMethods.push(PaymentMethod.CreditCard);
                if (CreditCards.filter((cc: DepositCreditCard) => cc.Id === 13)[0]) {
                    PaymentMethods.push(PaymentMethod.OtherCreditCard);
                }
            }
            const PaymentProviders: TSMap<number, PaymentProvider> = new TSMap<number, PaymentProvider>();
            if (Utils.isArrayNotEmpty(ApmPaymentMethods)) {
                ApmPaymentMethods.forEach((apm: ApmPaymentMethod) => {
                    if (Utils.isArrayNotEmpty(apm.PaymentProviders)) {
                        apm.PaymentProviders.forEach((pp: PaymentProvider) => {
                            PaymentMethods.push(pp.EWalletId);
                            PaymentProviders.set(pp.EWalletId, pp);
                        });
                    }
                });
            }
            const BanksCurrencies: Currency[] = [];
            const Banks: TSMap<Currency, BankDetail[]> = new TSMap<Currency, BankDetail[]>();
            const BankSelection: TSMap<Currency, BankDetail> = new TSMap<Currency, BankDetail>();
            if (Utils.isArrayNotEmpty(BankDetails)) {
                PaymentMethods.push(PaymentMethod.WireTransfer);
                BankDetails.forEach((bank: BankDetail) => {
                    if (BanksCurrencies.indexOf(bank.AssetName) < 0) {
                        BanksCurrencies.push(bank.AssetName);
                    }
                    const banks: BankDetail[] = Banks.get(bank.AssetName) || [];
                    banks.push(bank);
                    Banks.set(bank.AssetName, banks);
                });
                BanksCurrencies.sort((c1: Currency, c2: Currency) => Utils.compareString(c1, c2));
                Banks.forEach((banks: BankDetail[], currency: Currency) => {
                    banks.sort((b1: BankDetail, b2: BankDetail) => Utils.compareString(b1.Name, b2.Name));
                    BankSelection.set(currency, banks[0]);
                });
            }
            Platform.dispatch(SetDepositConfiguration({
                Currencies,
                CreditCards: CreditCards?.sort((cc1: DepositCreditCard, cc2: DepositCreditCard) => Utils.compareNumber(cc1?.Id, cc2?.Id)),
                PaymentMethods,
                PaymentProviders,
                PlatformUrl: BackToPlatformUrl,
                BanksCurrencies,
                Banks,
                BankSelection,
                Client: ClientDetails
            }));
            if (Utils.isNotNull(UrlAmount)) {
                Platform.dispatch(SetDepositAmount({
                    value: UrlAmount
                }));
            }
            Platform.dispatch(SetDepositCurrency({
                currency: InitialCurrency?.Name
            }));
            Platform.dispatch(SetCallMe({value: false}));
        }
        Platform.dispatch(HideLoader({}));
    }

    public onTryChangeCurrency = async ({currency}: CurrencyPayload) => {
        const {Amount, SelectedCurrency, Currencies} = Platform.reduxState<StoreState>().deposit;
        this._logger.debug(`Change currency to: ${currency}. Was currency: ${SelectedCurrency}. Amount: ${Amount}`);
        if (Amount) {
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const request: ConvertPaymentAmountRequest = {
                Amount,
                FromAssetId: Currencies.get(SelectedCurrency)?.Id,
                ToAssetId: Currencies.get(currency)?.Id
            };
            const answer: [HttpReject, ConvertPaymentAmountResponse] = await Utils.to(XhrManager.sendToBilling(request, "ConvertPaymentAmount"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.warn("Failed convert amount");
                XhrManager.notifyRejectReason(answer);
            } else {
                const {ConvertedAmount, HasErrors, Errors} = answer[1];
                if (HasErrors) {
                    this._logger.warn("Failed convert amount with errors");
                    Platform.dispatch(ShowPopup({
                        popup: {
                            type: PopupType.ERROR,
                            message: {trKey: TranslationKey.serverErrorGeneral},
                            showClose: true,
                            actions: [{type: PopupActionType.OK}]
                        }
                    }));
                } else {
                    Platform.dispatch(SetDepositAmount({value: ConvertedAmount}));
                    Platform.dispatch(SetDepositCurrency({currency}));
                }
            }
        } else {
            Platform.dispatch(SetDepositCurrency({currency}));
        }
    }

    public doAddSavedCreditCard = ({creditCard}: AddSavedCreditCardPayload) => {
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const {savedCreditCards} = Platform.reduxState<StoreState>().deposit;
        setTimeout(() => {
            const cleanedCreditCard: CreditCardState = {
                ...creditCard,
                cardNumber: creditCard.cardNumber.slice(-4),
                id: String((new Date()).getTime())
            }
            if (Array.isArray(savedCreditCards) && savedCreditCards.length) {
                // TODO Get actual response from server
                const newSavedCreditCards: CreditCardState[] = [...savedCreditCards];
                newSavedCreditCards.push(cleanedCreditCard);
                Platform.dispatch(SetSavedCreditCards({savedCreditCards: newSavedCreditCards}));
            } else {
                const newSavedCreditCards: CreditCardState[] = [cleanedCreditCard];
                Platform.dispatch(SetCreditCardId({creditCardId: newSavedCreditCards[0].id}));
                Platform.dispatch(SetSavedCreditCards({savedCreditCards: newSavedCreditCards}));
            }
            Platform.dispatch(HideLoader({}));
        }, 1000);
    }

    public doRemoveSavedCreditCard = ({creditCardId}: RemoveSavedCreditCardPayload) => {
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const {savedCreditCards} = Platform.reduxState<StoreState>().deposit;
        setTimeout(() => {
            if (Array.isArray(savedCreditCards) && savedCreditCards.length) {
                // TODO Get actual response from server
                const filteredCreditCards: CreditCardState[] = savedCreditCards.filter((cc: CreditCardState) => cc.id !== creditCardId);
                Platform.dispatch(SetCreditCardId({creditCardId: filteredCreditCards[0].id}));
                Platform.dispatch(SetSavedCreditCards({savedCreditCards: filteredCreditCards}));
            }
            Platform.dispatch(HideLoader({}));
        }, 1000);
    }

    public doSubmitDepositCC = async ({values}: DoSubmitDepositCCPayload) => {
        this._logger.debug("Submit deposit");
        const {Amount, SelectedCurrency, Currencies, PaymentMethod} = Platform.reduxState<StoreState>().deposit;
        const depositCurrency: DepositCurrency = Currencies?.values().filter((c: DepositCurrency) => c.Name === SelectedCurrency)[0];
        const FirstLastNames: string[] = values[FieldType.CardName]?.split(" ") || [];
        const century = new Date().getFullYear().toString().substring(0, 2);
        const expiration: string[] = values[FieldType.CardExpiration].split("/");

        await this.doSubmitDepositCCInternal(
            {
                amount: Amount,
                paymentAssetName: SelectedCurrency,
                currencyId: depositCurrency?.Id,
                cardNumber: values[FieldType.CardNumber].replace(/\s/g, ""),
                firstName: FirstLastNames[0],
                lastName: FirstLastNames[1],
                nameOnCard: values[FieldType.CardName],
                cvv: parseInt(values[FieldType.CardCvv]),
                expMonth: parseInt(expiration[0]),
                expYear: parseInt(`${century}${expiration[1]}`),
                promoCode: values[FieldType.PromotionCode],
            },
            {
                firstName: FirstLastNames[0],
                lastName: FirstLastNames[1],
                address: null,
                city: null,
                countryCode: null,
                emailAddress: null,
                langCode: null,
                phoneNumber: null,
                zipCode: null
            },
            null,
            null,
            PaymentMethod
        );
    }

    private doSubmitDepositCCInternal = async (
        paymentDetails: CreditCardPaymentDetails,
        clientDetails: CreditCardClientDetails,
        Metadata?: DepositByCreditCardMetaData,
        PaymentAuthResponse?: string,
        paymentMethod?: string | number
    ) => {
        const request: DepositByCreditCardRequest = {
            paymentDetails,
            clientDetails,
            Metadata,
            PaymentAuthResponse,
            CreditCardType: DepositUtil.CCFlowType(paymentMethod)
        };
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, DepositByCreditCardResponse] = await Utils.to(XhrManager.sendToBilling(request, "PerformDepositByCreditCard"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            this._logger.warn("Failed make deposit by CC");
            XhrManager.notifyRejectReason(answer);
        } else {
            const {HasErrors, Errors, RequestForm, ReceiptDetails, MetaData} = answer[1];
            if (HasErrors) {
                this._logger.debug("Deposit attempt with errors");
                const message: string[] = [];
                if (Utils.isArrayNotEmpty(Errors)) {
                    Errors.forEach((error: DepositByCreditCardError, index: number) => {
                        message.push(error.ErrorDescription);
                        if (error.ErrorCode === ErrorCode.DepositLimits) {
                            message.push("<br/><br/>");
                            message.push(Translations.text(TranslationKey.errorDepositLimits));
                        }
                        if (index < Errors.length - 1) {
                            message.push("<br/>");
                        }
                    });
                }
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            customValue: message.join("")
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            } else {
                if (RequestForm) {
                    this._logger.debug("Submitting form to: " + RequestForm.SubmitFormUrl);
                    Platform.storage().setItem(StorageKey.CCPaymentDetails, JSON.stringify(request.paymentDetails));
                    Platform.storage().setItem(StorageKey.CCClientDetails, JSON.stringify(request.clientDetails));
                    Platform.storage().setItem(StorageKey.CCMetaData, JSON.stringify(MetaData));
                    const {SubmitFormUrl, FormMethod, FieldData} = RequestForm;
                    const parameters: { [key: string]: string } = {};
                    if (Utils.isArrayNotEmpty(FieldData)) {
                        FieldData.forEach((field: {Key: string, Value: string}) => {
                            parameters[field.Key] = field.Value;
                        });
                    }
                    SubmitForm.submit(SubmitFormUrl, FormMethod, parameters);
                } else {
                    Platform.dispatch(SetReceiptDetails({details: ReceiptDetails}));
                    Platform.dispatch(NavigateTo({route: PageType.DepositSuccessful}));
                }
            }
        }
    }

    public doSubmitDepositAPM = async ({values}: DoSubmitDepositAPMPayload) => {
        const {core, deposit} = Platform.reduxState<StoreState>();
        const {Amount, SelectedCurrency, PaymentMethod, PaymentProviders} = deposit;
        const provider: PaymentProvider = PaymentProviders.get(PaymentMethod as number);
        if (provider) {
            const langCode: LangCode = LanguageUtil.languageCode();
            const request: DepositByAPMRequest = {
                Amount,
                Currency: SelectedCurrency,
                EWalletId: provider.EWalletId,
                ExternalPaymentProviderId: provider.ExternalPaymentProviderId,
                UrlRedirect: `https://${window.location.hostname}/?eVerify=true&theme=${core.theme}&lang=${langCode}`,
                PromoCode: values[FieldType.PromotionCode],
                ClientDetails: DepositUtil.BuildAPMClientDetails(PaymentMethod, values)
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, DepositByAPMResponse] = await Utils.to(XhrManager.sendToBilling(request, "PerformExternalAPICall"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.warn("Failed make deposit by APM");
                XhrManager.notifyRejectReason(answer);
            } else {
                const {TransRefNum, GeneratedUrl, ErrorCode, ErrorDescription} = answer[1];
                if (ErrorCode > 0) {
                    Platform.dispatch(ShowPopup({
                        popup: {
                            type: PopupType.ERROR,
                            message: {
                                customValue: ErrorDescription,
                                trKey: TranslationKey.serverErrorGeneral
                            },
                            showClose: true,
                            icon: PopupIconType.ERROR,
                            actions: [{type: PopupActionType.OK}]
                        }
                    }));
                } else {
                    Platform.storage().setItem(StorageKey.APMTransRefNum, TransRefNum);
                    if (WebUtil.isMobile()) {
                        Platform.environment().redirect(GeneratedUrl);
                    } else {
                        Platform.dispatch(ShowPopup({
                            popup: {
                                type: PopupType.INFO,
                                message: {
                                    trKey: TranslationKey.redirectToAPM,
                                    params: [Parameter.Of(TranslationParam.name, provider.DisplayName)]
                                },
                                showClose: true,
                                iconBase64: provider.ImageBase64,
                                actions: [{type: PopupActionType.OK, text: {trKey: TranslationKey.continue_}, action: () => {
                                        Platform.environment().openWindow(GeneratedUrl);
                                    }}]
                            }
                        }));
                    }
                }
            }
        } else {
            this._logger.warn("Payment provider absent for id: " + PaymentMethod);
        }
    }

    private doVerifyDepositAPM = async () => {
        const TransRefNum: string = await Platform.storage().getItem(StorageKey.APMTransRefNum);
        if (TransRefNum) {
            this._logger.debug("Verify APM by TransRefNum: " + TransRefNum);
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, DepositVerifyAPMResponse] = await Utils.to(XhrManager.sendToBilling(TransRefNum, "GetEWalletDepositStatusByReferenceNumber", true));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.warn("Failed verify APM");
                XhrManager.notifyRejectReason(answer);
            } else {
                // TODO Show receipt
            }
        } else {
            this._logger.warn("APM TransRefNum absent in LS");
        }
    }
}
